/**
 * Arz / talep çubuğu — kütüphaneye projeden eklenen grafik tipi.
 *
 * Malzeme başına iki çubuk yan yana: üretim + stok ÜST ÜSTE (arz), yanında
 * talep (anamontaj tüketimi). Kütüphanenin `stacked` seçeneği tüm çubukları
 * tek yığına koyduğu için talep de arzın üstüne biniyordu; burada yığın
 * çubuk başına (`stackId`) veriliyor. Kütüphanede ölçü başına yığın grubu
 * eklenirse bu tip kaldırılıp `stacked-bar`'a dönülebilir.
 *
 * Satır: { malzeme, uretim, stok, talep, arz, kritik } — api.js `arzTalep`.
 */
import { registerChart, useChartTheme } from "@smartopt/charts";
import {
  Bar,
  BarChart,
  CartesianGrid,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

export const ARZ_TALEP = "arz-talep";

/** Mailde gelen örnek grafikle aynı renkler. */
const URETIM = "#2f6fba";
const STOK = "#56a24a";
const TALEP = "#e0a020";
const KRITIK = "#ef4444";

function ArzTalep({ config, data }) {
  const theme = useChartTheme();
  const o = config.options ?? {};
  const fmt = new Intl.NumberFormat(theme.locale, { maximumFractionDigits: 0 });
  const eksen = { fill: theme.axis, fontSize: o.axisStyle?.size ?? 10 };
  const etiket = o.labelStyle?.size ?? 11;

  if (!data?.length) {
    return (
      <div
        style={{
          height: "100%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          color: theme.muted,
          fontSize: 13,
        }}
      >
        Bu grupta üretimi ya da talebi olan malzeme yok
      </div>
    );
  }

  /**
   * X ekseni etiketi yatay ve iki satır: üstte malzeme kodu, altta tanım
   * ("8001340261" / "Beyaz F14 Ön"). Eğik yazı eksene çok yer ayırtıyor,
   * grafiği küçültüyordu.
   */
  const MalzemeEtiketi = ({ x, y, payload }) => {
    const s = String(payload?.value ?? "");
    const i = s.indexOf(" ");
    const kod = i > 0 ? s.slice(0, i) : s;
    const ad = i > 0 ? s.slice(i + 1) : "";
    return (
      <text x={x} y={y} textAnchor="middle" fill={eksen.fill} fontSize={eksen.fontSize}>
        <tspan x={x} dy="0.9em" fontWeight={600}>
          {kod}
        </tspan>
        {ad && (
          <tspan x={x} dy="1.2em">
            {ad}
          </tspan>
        )}
      </text>
    );
  };

  /** Yığının tepesine toplam arz, altında yetmiyorsa KRITIK. */
  const ToplamEtiketi = ({ x, y, width, index }) => {
    const r = data[index];
    if (!r) return null;
    const cx = x + width / 2;
    return (
      <g>
        {r.kritik && (
          <text x={cx} y={y - etiket - 8} textAnchor="middle" fill={KRITIK} fontSize={etiket} fontWeight={700}>
            KRITIK
          </text>
        )}
        <text x={cx} y={y - 4} textAnchor="middle" fill={theme.label} fontSize={etiket}>
          {fmt.format(r.arz)}
        </text>
      </g>
    );
  };

  /**
   * Araç ipucu: çubuklara ek olarak arz toplamı ve ufuk sonu stoğu —
   * "ufkun sonunda ne kalıyor" sorusu grafikte değil burada cevaplanır.
   */
  const Ipucu = ({ active, payload }) => {
    const r = active && payload?.[0]?.payload;
    if (!r) return null;
    const satir = (ad, v, renk) => (
      <div style={{ display: "flex", justifyContent: "space-between", gap: 16 }}>
        <span style={{ color: renk ?? theme.muted }}>{ad}</span>
        <span style={{ color: theme.text, fontWeight: 600 }}>{fmt.format(v)}</span>
      </div>
    );
    return (
      <div
        style={{
          background: theme.tooltipBg,
          border: `1px solid ${theme.tooltipBorder}`,
          borderRadius: 6,
          padding: "10px 12px",
          fontSize: 15,
          minWidth: 280,
        }}
      >
        <div style={{ color: theme.text, fontWeight: 700, marginBottom: 6 }}>{r.malzeme}</div>
        {satir("Üretim (ufuk toplamı)", r.uretim, URETIM)}
        {satir("Başlangıç stoğu", r.stok, STOK)}
        {satir("Arz (üretim + stok)", r.arz)}
        {satir("Talep (ufuk toplamı)", r.talep, TALEP)}
        <div style={{ borderTop: `1px solid ${theme.border}`, margin: "6px 0" }} />
        {satir("Ufuk sonu stoğu", r.stok_son)}
        {r.kritik && (
          <div style={{ color: KRITIK, fontWeight: 700, marginTop: 4 }}>KRITIK — arz talebi karşılamıyor</div>
        )}
      </div>
    );
  };

  return (
    <ResponsiveContainer width="100%" height="100%">
      <BarChart
        data={data}
        margin={{ top: etiket * 2 + 14, right: 12, left: 4, bottom: 4 }}
        barGap={2}
        barCategoryGap="22%"
      >
        <CartesianGrid stroke={theme.grid} strokeDasharray="3 3" vertical={false} />
        <XAxis
          dataKey="malzeme"
          tick={<MalzemeEtiketi />}
          interval={0}
          height={o.xAxisHeight ?? 50}
          stroke={theme.border}
        />
        <YAxis
          tick={eksen}
          width={o.yAxisWidth ?? 44}
          stroke={theme.border}
          tickFormatter={(v) => fmt.format(v)}
        />
        <Tooltip cursor={{ fill: "rgba(255,255,255,0.06)" }} content={<Ipucu />} />
        <Legend
          verticalAlign="top"
          align="right"
          wrapperStyle={{ fontSize: o.legendStyle?.size ?? 11, color: theme.text, top: 0 }}
        />
        {/* Lejant adları değerin kapsamını da söylüyor — üst şeritteki
            "ufuk" açıklamasıyla aynı dil. */}
        <Bar dataKey="uretim" name="Üretim (ufuk toplamı)" stackId="arz" fill={URETIM} isAnimationActive={false} />
        <Bar dataKey="stok" name="Başlangıç stoğu" stackId="arz" fill={STOK} isAnimationActive={false}>
          <LabelList dataKey="arz" content={ToplamEtiketi} />
        </Bar>
        <Bar dataKey="talep" name="Talep (ufuk toplamı)" fill={TALEP} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

registerChart(ARZ_TALEP, ArzTalep);
